import { useAction, useDidMount, usePageQuery } from '@agro-club/frontend-shared'
import CompanyActions from 'modules/domain/company/duck'
import * as managers from 'modules/domain/company/managers'
import CompanySelectors from 'modules/domain/company/selectors'
import {
  BranchCompany,
  Company,
  CompanyListRequestFilter,
  CompanyMetaData,
  CompanyRelation,
} from 'modules/domain/company/types'
import { Progress, ResourceHook } from 'modules/types'
import { makeCancellableResourceListHook } from 'modules/utils/helpers'
import { createItemByIdHook } from 'modules/utils/helpers/createItemByIdHook'
import { makeCancelable } from 'modules/utils/httpClient'
import { useSelector } from 'react-redux'

export const useCompanyList: ResourceHook<
  Company[],
  [
    {
      filter?: CompanyListRequestFilter
      pageSize?: number
      cached?: boolean
    },
  ]
> = ({
  filter,
  pageSize,
  cached = true,
}: {
  filter?: CompanyListRequestFilter
  pageSize?: number
  cached?: boolean
} = {}) => {
  const progress = useSelector(CompanySelectors.listFetchProgress)
  const list = useSelector(CompanySelectors.companyList)
  const page = usePageQuery()
  const params: { filter?: CompanyListRequestFilter; page?: number; pageSize?: number } = {}

  if (filter) params.filter = filter
  if (page) params.page = page
  if (pageSize !== undefined) params.pageSize = pageSize

  const fetchAction = useAction(CompanyActions.listRequested, params)

  useDidMount(() => {
    if (progress !== Progress.SUCCESS || !cached) {
      fetchAction()
    }
  })

  return [progress, list]
}

export const useBranchCompaniesById: ResourceHook<
  {
    companies: BranchCompany[]
    permissions: CompanyRelation[]
    ids: string[]
  },
  [string]
> = (id: string) => {
  const branchCompaniesList = useSelector((state) => CompanySelectors.branchCompaniesById(state, id))
  const branchCompaniesIdsList = useSelector((state) => CompanySelectors.branchCompaniesIdsById(state, id))
  const branchCompaniesPermissions = useSelector((state) => CompanySelectors.branchCompaniesPermissionsById(state, id))

  const fetchProgress = useSelector((state) => CompanySelectors.branchCompaniesFetchProgressById(state, id))
  const fetchAction = useAction(CompanyActions.branchCompaniesRequested, id)

  useDidMount(() => {
    if (id) {
      fetchAction()
    }
  })

  return [
    fetchProgress,
    { companies: branchCompaniesList, ids: branchCompaniesIdsList, permissions: branchCompaniesPermissions },
  ]
}

export const useCompanyListByIds: ResourceHook<
  Company[],
  [{ filter?: CompanyListRequestFilter; company_ids: string[] }]
> = (
  {
    filter,
    company_ids,
  }: {
    filter?: CompanyListRequestFilter
    company_ids: string[]
  } = { company_ids: [] },
): [Progress, Company[]] => {
  const companyList = useSelector(CompanySelectors.companyListByIds)
  const fetchProgress = useSelector(CompanySelectors.listByIdsFetchProgress)
  const fetchAction = useAction(CompanyActions.listByIdsRequested, { company_ids, filters: filter })
  useDidMount(() => {
    fetchAction({ company_ids, filters: filter })
  })
  return [fetchProgress, companyList]
}

export const useCompanyListByExcludedIds: ResourceHook<
  Company[],
  [{ filter?: CompanyListRequestFilter; exclude_companies: string[] }]
> = (
  {
    filter,
    exclude_companies,
  }: {
    filter?: CompanyListRequestFilter
    exclude_companies: string[]
  } = { exclude_companies: [] },
) => {
  const companyList = useSelector(CompanySelectors.companyListByExcludedIds)
  const fetchProgress = useSelector(CompanySelectors.listByExcludedIdsFetchProgress)
  const fetchAction = useAction(CompanyActions.listByExcludedIdsRequested, { exclude_companies, filters: filter })
  useDidMount(() => {
    fetchAction({ exclude_companies, filters: filter })
  })
  return [fetchProgress, companyList]
}

export const useCompanyById = createItemByIdHook(CompanySelectors, CompanyActions.itemRequested)

export const useProducersOptions = makeCancellableResourceListHook(makeCancelable(managers.getProducersList))
export const useSellersOptions = makeCancellableResourceListHook(makeCancelable(managers.getSellersList))
export const useInventoryExchangeProducersOptions = makeCancellableResourceListHook(
  makeCancelable(managers.getInventoryExchangeProducersList),
)
export const useInventoryExchangeDistributorsOptions = makeCancellableResourceListHook(
  makeCancelable(managers.getInventoryExchangeDistibutorsList),
)

export const useCompanyMetaData: ResourceHook<CompanyMetaData, [string]> = (id: string) => {
  const progress = useSelector(CompanySelectors.metaFetchProgress)
  const company = useSelector((state) => CompanySelectors.company(state, id))
  const fetchAction = useAction(CompanyActions.itemMetaDataRequested, id, company)

  useDidMount(() => {
    if (!company?.metadata) {
      fetchAction()
    }
  })

  return [progress, company?.metadata]
}
